const PENDING_SIGN_UP_EXAM_SELECTION_KEY = "pending_sign_up_exam_selection";
const PENDING_SELECTION_MAX_AGE_MS = 2 * 60 * 60 * 1000;

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export interface PendingSignUpExamSelection {
  codebookYear: number | null;
  stateCode: string;
}

interface StoredPendingSignUpExamSelection extends PendingSignUpExamSelection {
  appType: string;
  createdAt: number;
}

interface AuthenticationReturn {
  afterSignIn: boolean;
  afterSignUp: boolean;
  fromSignUp: boolean;
}

export type PostAuthExamSelectionResolution =
  | { selection: PendingSignUpExamSelection; source: "pending" }
  | { selection: null; source: "account" | "none" | "unknown" };

function getSessionStorage(): StorageLike | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function removePendingSelection(storage: StorageLike | null): void {
  try {
    storage?.removeItem(PENDING_SIGN_UP_EXAM_SELECTION_KEY);
  } catch {
    return;
  }
}

export function clearPendingSignUpExamSelection(
  storage: StorageLike | null = getSessionStorage(),
): void {
  removePendingSelection(storage);
}

export function savePendingSignUpExamSelection(
  appType: string,
  selection: PendingSignUpExamSelection,
  storage: StorageLike | null = getSessionStorage(),
  now = Date.now(),
): void {
  if (!selection.stateCode) return;

  const value: StoredPendingSignUpExamSelection = {
    ...selection,
    appType,
    createdAt: now,
  };

  try {
    storage?.setItem(PENDING_SIGN_UP_EXAM_SELECTION_KEY, JSON.stringify(value));
  } catch {
    return;
  }
}

export function readPendingSignUpExamSelection(
  appType: string,
  storage: StorageLike | null = getSessionStorage(),
  now = Date.now(),
): PendingSignUpExamSelection | null {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(PENDING_SIGN_UP_EXAM_SELECTION_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const value = JSON.parse(raw) as Partial<StoredPendingSignUpExamSelection>;
    const codebookYearIsValid =
      value.codebookYear === null ||
      (typeof value.codebookYear === "number" &&
        Number.isFinite(value.codebookYear));
    const createdAtIsValid =
      typeof value.createdAt === "number" &&
      value.createdAt <= now &&
      now - value.createdAt <= PENDING_SELECTION_MAX_AGE_MS;

    if (
      value.appType !== appType ||
      typeof value.stateCode !== "string" ||
      !value.stateCode ||
      !codebookYearIsValid ||
      !createdAtIsValid
    ) {
      removePendingSelection(storage);
      return null;
    }

    return {
      codebookYear: value.codebookYear ?? null,
      stateCode: value.stateCode,
    };
  } catch {
    removePendingSelection(storage);
    return null;
  }
}

export function isSignUpAuthenticationReturn({
  afterSignIn,
  afterSignUp,
  fromSignUp,
}: AuthenticationReturn): boolean {
  return afterSignUp || (afterSignIn && fromSignUp);
}

export function resolvePostAuthExamSelection(
  accountStateCode: string | null | undefined,
  pendingSelection: PendingSignUpExamSelection | null,
  accountProfileLoaded = true,
  accountSelectionIsExplicit = Boolean(accountStateCode),
): PostAuthExamSelectionResolution {
  if (!accountProfileLoaded) {
    return { selection: null, source: "unknown" };
  }
  if (accountSelectionIsExplicit && accountStateCode) {
    return { selection: null, source: "account" };
  }
  if (pendingSelection?.stateCode) {
    return { selection: pendingSelection, source: "pending" };
  }
  return { selection: null, source: "none" };
}
